// components/templates/sections/VouchersSection.tsx
'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface Voucher {
  type: 'online' | 'gift';
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  icon: string;
}

interface VouchersSectionProps {
  leftImage: string;
  rightImage: string;
  onlineVoucher: Voucher;
  giftVoucher: Voucher;
  design?: any;
}

export default function VouchersSection({
  leftImage,
  rightImage,
  onlineVoucher,
  giftVoucher,
  design: propDesign,
}: VouchersSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const primaryColor = design?.colors?.primary || '#E07A5F';
  const bgColor = design?.colors?.background || '#FFFFFF';
  const borderColor = design?.colors?.border || '#E9ECEF';
  const boxShadow = design?.effects?.boxShadow || '0 5px 20px rgba(0,0,0,0.05)';
  const btnStyle = design?.buttons?.outline || {
    class: 'tf-button style3',
    backgroundColor: 'transparent',
    borderColor: 'var(--primary)',
    textColor: 'var(--primary)',
    borderRadius: '30px',
    padding: '10px 25px',
  };
  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';

  const cardStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    border: `1px solid ${borderColor}`,
    boxShadow: boxShadow,
    borderRadius: design?.effects?.borderRadius || '8px',
    padding: '1.5rem',
    textAlign: 'center',
  };
  const iconStyle: React.CSSProperties = {
    filter: `brightness(0) saturate(100%) invert(30%) sepia(80%) saturate(2000%) hue-rotate(340deg)`, // approx primary
  };
  const titleStyle: React.CSSProperties = {
    fontFamily: headingFont,
    color: design?.colors?.text || '#1E1E1E',
    marginTop: '1rem',
  };
  const descStyle: React.CSSProperties = {
    fontFamily: bodyFont,
    color: design?.colors?.textLight || '#6C757D',
    margin: '1rem 0',
  };
  const buttonStyle: React.CSSProperties = {
    backgroundColor: btnStyle.backgroundColor,
    border: btnStyle.borderColor ? `1px solid ${btnStyle.borderColor}` : 'none',
    color: btnStyle.textColor,
    padding: btnStyle.padding,
    borderRadius: btnStyle.borderRadius,
    cursor: 'pointer',
    display: 'inline-block',
    textDecoration: 'none',
  };

  return (
    <section className="vouchers" style={{ padding: '60px 0' }}>
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto' }}>
        <div className="row">
          <div className="col-12">
            <div className="vouchers-content" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <div className="item image" data-aos="fade-right" style={{ flex: 1, minWidth: '200px' }}>
                <Image src={leftImage} alt="" width={300} height={300} style={{ width: '100%', height: 'auto' }} />
              </div>
              <div className="item content" style={cardStyle}>
                <Image src={onlineVoucher.icon} alt="" width={50} height={50} style={iconStyle} />
                <h4 style={titleStyle}>{onlineVoucher.title}</h4>
                <p style={descStyle}>{onlineVoucher.description}</p>
                <Link href={onlineVoucher.buttonLink} className={btnStyle.class} style={buttonStyle}>
                  {onlineVoucher.buttonText}
                </Link>
              </div>
              <div className="item content" style={cardStyle}>
                <Image src={giftVoucher.icon} alt="" width={50} height={50} style={iconStyle} />
                <h4 style={titleStyle}>{giftVoucher.title}</h4>
                <p style={descStyle}>{giftVoucher.description}</p>
                <Link href={giftVoucher.buttonLink} className={btnStyle.class} style={buttonStyle}>
                  {giftVoucher.buttonText}
                </Link>
              </div>
              <div className="item image" data-aos="fade-left" style={{ flex: 1, minWidth: '200px' }}>
                <Image src={rightImage} alt="" width={300} height={300} style={{ width: '100%', height: 'auto' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}